/**
 * Data Attributes API Client
 * PROJ-042 Phase 2 - Data Attributes Frontend
 */

import { fetchWithAuth } from './http-client';
import { parseStandardApiResponse } from './standard-api';
import type {
  DataAttribute,
  CreateDataAttributePayload,
  UpdateDataAttributePayload,
  NodeDataAttribute,
} from '@/types/data-attributes';

const API_BASE = (import.meta.env.VITE_API_URL || '/api/v1').replace(/\/$/, '');
const API_ENV_VAR = 'VITE_API_URL';

// Backend schema (camelCase - Drizzle returns camelCase)
interface DataAttributeBackend {
  code: string;
  name: string;
  description?: string;
  category: string;
  icon?: string;
  defaultClassificationCode?: string;
  defaultClassification?: {
    code: string;
    name: string;
    level: string;
    colorCode: string;
  };
  isSystem: boolean;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

interface NodeDataAttributeBackend {
  dataAttributeCode: string;
  attribute: DataAttributeBackend;
  classificationCode: string;
  classification: {
    code: string;
    name: string;
    level: string;
    colorCode: string;
  };
}

// Transform backend data attribute to frontend format
// Note: Backend already returns camelCase from Drizzle, so minimal transformation needed
function transformDataAttribute(backend: DataAttributeBackend): DataAttribute {
  return {
    code: backend.code,
    name: backend.name,
    description: backend.description,
    category: backend.category as any,
    icon: backend.icon,
    defaultClassificationCode: backend.defaultClassificationCode,
    defaultClassification: backend.defaultClassification
      ? {
          code: backend.defaultClassification.code,
          name: backend.defaultClassification.name,
          level: backend.defaultClassification.level,
          colorCode: backend.defaultClassification.colorCode,
        }
      : undefined,
    isSystem: backend.isSystem,
    isActive: backend.isActive,
    createdAt: backend.createdAt,
    updatedAt: backend.updatedAt,
  };
}

// Transform node data attribute to frontend format
function transformNodeDataAttribute(backend: NodeDataAttributeBackend): NodeDataAttribute {
  return {
    dataAttributeCode: backend.dataAttributeCode,
    dataAttribute: transformDataAttribute(backend.attribute),
    classificationCode: backend.classificationCode,
    classification: {
      code: backend.classification.code,
      name: backend.classification.name,
      level: backend.classification.level,
      colorCode: backend.classification.colorCode,
    },
  };
}

// ============================================================================
// Data Attributes CRUD
// ============================================================================

export async function getDataAttributes(): Promise<DataAttribute[]> {
  const response = await fetchWithAuth(`${API_BASE}/data-attributes`, {
    context: 'Listado de atributos de datos',
    envVar: API_ENV_VAR,
    baseUrl: API_BASE,
  });

  const { data } = await parseStandardApiResponse<DataAttributeBackend[]>(
    response,
    'Listado de atributos de datos'
  );

  return data.map(transformDataAttribute);
}

export async function getDataAttribute(code: string): Promise<DataAttribute> {
  const response = await fetchWithAuth(`${API_BASE}/data-attributes/${code}`, {
    context: 'Detalle de atributo de datos',
    envVar: API_ENV_VAR,
    baseUrl: API_BASE,
  });

  const { data } = await parseStandardApiResponse<DataAttributeBackend>(
    response,
    'Detalle de atributo de datos'
  );

  return transformDataAttribute(data);
}

export async function createDataAttribute(
  payload: CreateDataAttributePayload
): Promise<DataAttribute> {
  // Backend expects camelCase keys
  const backendPayload = {
    code: payload.code,
    name: payload.name,
    description: payload.description,
    category: payload.category,
    icon: payload.icon,
    defaultClassificationCode: payload.defaultClassificationCode,
  };

  const response = await fetchWithAuth(`${API_BASE}/data-attributes`, {
    method: 'POST',
    body: JSON.stringify(backendPayload),
    context: 'Crear atributo de datos',
    envVar: API_ENV_VAR,
    baseUrl: API_BASE,
  });

  const { data } = await parseStandardApiResponse<DataAttributeBackend>(
    response,
    'Crear atributo de datos'
  );

  return transformDataAttribute(data);
}

export async function updateDataAttribute(
  code: string,
  payload: UpdateDataAttributePayload
): Promise<DataAttribute> {
  // Backend expects camelCase keys
  const backendPayload: any = {};

  if (payload.name !== undefined) backendPayload.name = payload.name;
  if (payload.description !== undefined) backendPayload.description = payload.description;
  if (payload.category !== undefined) backendPayload.category = payload.category;
  if (payload.icon !== undefined) backendPayload.icon = payload.icon;
  if (payload.defaultClassificationCode !== undefined)
    backendPayload.defaultClassificationCode = payload.defaultClassificationCode;

  const response = await fetchWithAuth(`${API_BASE}/data-attributes/${code}`, {
    method: 'PATCH',
    body: JSON.stringify(backendPayload),
    context: 'Actualizar atributo de datos',
    envVar: API_ENV_VAR,
    baseUrl: API_BASE,
  });

  const { data } = await parseStandardApiResponse<DataAttributeBackend>(
    response,
    'Actualizar atributo de datos'
  );

  return transformDataAttribute(data);
}

export async function deleteDataAttribute(code: string): Promise<void> {
  const response = await fetchWithAuth(`${API_BASE}/data-attributes/${code}`, {
    method: 'DELETE',
    context: 'Eliminar atributo de datos',
    envVar: API_ENV_VAR,
    baseUrl: API_BASE,
  });

  await parseStandardApiResponse<void>(response, 'Eliminar atributo de datos');
}

// ============================================================================
// Node Data Attributes (for workflow nodes)
// ============================================================================

export async function getNodeDataAttributes(nodeId: string): Promise<NodeDataAttribute[]> {
  const response = await fetchWithAuth(`${API_BASE}/data-attributes/node/${nodeId}`, {
    context: 'Atributos de datos del nodo',
    envVar: API_ENV_VAR,
    baseUrl: API_BASE,
  });

  const { data } = await parseStandardApiResponse<NodeDataAttributeBackend[]>(
    response,
    'Atributos de datos del nodo'
  );

  return data.map(transformNodeDataAttribute);
}

export async function addNodeDataAttribute(
  nodeId: string,
  attributeCode: string,
  classificationCode?: string
): Promise<NodeDataAttribute> {
  const response = await fetchWithAuth(`${API_BASE}/data-attributes/node/${nodeId}`, {
    method: 'POST',
    body: JSON.stringify({
      attributeCode,
      classificationCode,
    }),
    context: 'Agregar atributo al nodo',
    envVar: API_ENV_VAR,
    baseUrl: API_BASE,
  });

  const { data } = await parseStandardApiResponse<NodeDataAttributeBackend>(
    response,
    'Agregar atributo al nodo'
  );

  return transformNodeDataAttribute(data);
}

export async function updateNodeDataAttribute(
  nodeId: string,
  attributeCode: string,
  classificationCode: string
): Promise<NodeDataAttribute> {
  const response = await fetchWithAuth(
    `${API_BASE}/data-attributes/node/${nodeId}/${attributeCode}`,
    {
      method: 'PATCH',
      body: JSON.stringify({ classificationCode }),
      context: 'Actualizar clasificación del atributo',
      envVar: API_ENV_VAR,
      baseUrl: API_BASE,
    }
  );

  const { data } = await parseStandardApiResponse<NodeDataAttributeBackend>(
    response,
    'Actualizar clasificación del atributo'
  );

  return transformNodeDataAttribute(data);
}

export async function removeNodeDataAttribute(
  nodeId: string,
  attributeCode: string
): Promise<void> {
  const response = await fetchWithAuth(
    `${API_BASE}/data-attributes/node/${nodeId}/${attributeCode}`,
    {
      method: 'DELETE',
      context: 'Eliminar atributo del nodo',
      envVar: API_ENV_VAR,
      baseUrl: API_BASE,
    }
  );

  await parseStandardApiResponse<void>(response, 'Eliminar atributo del nodo');
}
